import { Button, message } from 'antd';
import { useTranslate } from '@tolgee/react';
import { NewSelectType, SubTypes } from './NewSelectType';
import { TypeSource } from 'views/Conversation/Sider/ToDoBundle/NewTodo';
import { NewNoteInput } from './NewNoteInput';
import { useCallback, useRef, useState } from 'react';
import { NewRelatedOrders } from './NewRelatedOrders';
import { NewRelatedItems } from './NewRelatedItems';
import { Assign } from '../Assign';
import { createTodoInstance, getTodoDetail } from 'views/Conversation/Sider/ToDoBundle/api';
import { useQueryClient } from '@tanstack/react-query';
import { queries } from 'services/queries';
import { getTradeDefaultItem, getTypeName } from '../utils';
import { isNil } from 'lodash-es';
import { convertTextAndImageToMessage } from 'views/Conversation/Sider/ToDoBundle/helper';
import { useAuthorization } from 'components/Authorization';
import { NewReturnOrders } from './NewReturnOrders';
import { DefaultButton } from '../../DefaultButton';

interface NewTicketProps {
  platform: General.PlatformUpperCase;
  typeSource: TypeSource;
  storeId: string;
  conversationId: string;
  externalBuyerId: string;
  buyerId: string;
  taskId: number;
  onCancel: () => void;
  onSuccess: (newTicket?: Todo.TodoInstance) => void;
}

export const NewTicket = (props: NewTicketProps) => {
  const {
    platform,
    typeSource,
    storeId,
    conversationId,
    externalBuyerId,
    buyerId,
    taskId,
    onCancel,
    onSuccess,
  } = props;
  const { t } = useTranslate();
  const queryClient = useQueryClient();
  const { payload } = useAuthorization<Oversea.Token.Payload>();
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [note, setNote] = useState<{ text: string; images: string[] }>({
    text: '',
    images: [],
  });
  const [relatedTradeId, setRelatedTradeId] = useState('');
  const [relatedSku, setRelatedSku] = useState<Todo.RelatedSku[]>([]);
  const [assigneeId, setAssigneeId] = useState<string | undefined>(payload?.id?.toString());
  const [type, setType] = useState<Todo.Type | undefined>(undefined);
  const [subTypeValues, setSubTypeValues] = useState<SubTypes>([]);
  const [relatedReturnId, setRelatedReturnId] = useState<string | undefined>(undefined);

  const resetState = () => {
    setNote({ text: '', images: [] });
    setRelatedTradeId('');
    setRelatedSku([]);
    setRelatedReturnId(undefined);
    setAssigneeId(payload?.id?.toString());
    setType(undefined);
    setRelatedReturnId(undefined);
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      if (isNil(type)) {
        message.error(t('choose_todo_type'));
        return;
      }

      const typeName = getTypeName(type, typeSource, '');

      let subType;
      let subType2;
      let subType3;
      if (subTypeValues.length > 0) {
        subType = subTypeValues[0];
        subType2 = subTypeValues[1];
        subType3 = subTypeValues[2];
      }

      const payload: Todo.CreateTodoParams & { relatedSku?: Todo.RelatedSku[] } = {
        type,
        typeName,
        description: convertTextAndImageToMessage(note.text, note.images),
        storeId,
        tradeBuyerId: externalBuyerId,
        operator: assigneeId || '',
        taskId,
        relatedTradeId: relatedTradeId || '',
        relatedItemId: '',
        relatedReturnId: relatedReturnId || '',
        relatedMsgId: '',
        subType,
        subType2,
        subType3,
      };

      if (relatedSku.length > 0) {
        payload.relatedSku = relatedSku;
      }

      const { data } = await createTodoInstance(payload as Todo.CreateTodoParams);
      const todoId = data?.data?.id;
      if (!todoId) {
        throw new Error('todoId is empty');
      }
      let newDetail: Todo.TodoInstance | undefined;
      try {
        newDetail = await getTodoDetail(String(todoId));
      } catch (err) {
        if (buyerId) {
          const listParams = { storeId, buyerId, tradeBuyerId: externalBuyerId };
          await queryClient.invalidateQueries({
            queryKey: queries.tickets.list(listParams).queryKey,
          });
        }
      }

      message.success(t('create_todo_success'));
      resetState();
      onSuccess(newDetail);
    } catch {
      message.error(t('phrase.保存失败，请稍后再试'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSelectOrder = useCallback((trade: Conversation.Trade.Vo | null) => {
    setRelatedTradeId(trade?.externalTradeId ?? '');
    setRelatedSku(getTradeDefaultItem(trade ?? undefined));
  }, []);

  const handleClearOrder = useCallback(() => {
    setRelatedTradeId('');
    setRelatedSku([]);
  }, []);

  return (
    <div className="flex-1 flex flex-col overflow-hidden bg-[var(--fett-bg-app)]">
      <div
        className="flex-1 flex flex-col px-3 py-4 overflow-y-auto bg-[var(--fett-bg-elevated)] gap-2"
        ref={scrollContainerRef}
      >
        <NewSelectType
          storeId={storeId}
          taskId={taskId}
          typeSource={typeSource}
          platform={platform}
          type={type}
          onChange={setType}
          subType={subTypeValues}
          onChangeSubType={setSubTypeValues}
        />
        <NewNoteInput
          platform={platform}
          storeId={storeId}
          conversationId={conversationId}
          text={note.text}
          images={note.images}
          onChange={setNote}
        />
        <NewRelatedOrders
          platform={platform}
          externalBuyerId={externalBuyerId}
          storeId={storeId}
          onSelect={handleSelectOrder}
          onClear={handleClearOrder}
        />
        <NewRelatedItems
          relatedTradeId={relatedTradeId}
          platform={platform}
          storeId={storeId}
          relatedSku={relatedSku}
          externalBuyerId={externalBuyerId}
          ready={Boolean(platform !== 'FACEBOOK')}
          onChange={setRelatedSku}
        />
        <NewReturnOrders
          relatedTradeId={relatedTradeId}
          storeId={storeId}
          platform={platform}
          externalBuyerId={externalBuyerId}
          readonly={Boolean(platform !== 'FACEBOOK')}
          onChange={setRelatedReturnId}
          relatedReturnId={relatedReturnId}
        />
        <Assign storeId={storeId} value={assigneeId} onChange={setAssigneeId} />
      </div>
      <div className="flex-shrink-0 flex items-center justify-end bg-[var(--fett-bg-elevated)] py-2 px-4 shadow-tt">
        <div className="flex items-center gap-4">
          <DefaultButton onClick={onCancel}>{t('translation.取消')}</DefaultButton>
          <Button type="primary" htmlType="submit" onClick={handleSubmit} loading={isSubmitting}>
            {t('ViewStartups.reset.form.submit.btn')}
          </Button>
        </div>
      </div>
    </div>
  );
};
